/**
 * Feature registry (R1 → R2 structure). Every non-core screen lives in
 * `src/features/<name>/` as a tiny eager `manifest.ts` (entry point, unlock, flag, the view
 * kinds it provides, optional map hooks) and a lazy `index.tsx` chunk with the views.
 * Scenes and the HUD only talk to this registry, never to a feature's code directly.
 */
import type { JSX } from 'preact';
import { economy, type RemoteConfig } from '../config/content.js';
import type { AppContext } from '../game/context.js';
import type { DeepLink } from '../services/deeplinks.js';
import type { PlayerState } from '../services/state.js';
import type { ModalEntry, RailEntry, RailIcon } from '../ui/bridge.js';
import type { ModalCtx } from '../ui/modals.js';

export interface ViewProps<P = unknown, R = unknown> {
  e: ModalEntry & { props: P };
  close: (r: R) => void;
  c: ModalCtx;
}

export type View = (p: ViewProps<never, never>) => JSX.Element | null;

export interface FeatureModule {
  views: Record<string, View>;
  /** map-show hook (results cards, deliveries, offers); runs only when the manifest says it is due */
  onMapShow?: (a: AppContext) => Promise<void>;
  /** SO-05 deep link this feature handles */
  onLink?: (a: AppContext, link: DeepLink) => Promise<void>;
}

export interface FeatureManifest {
  id: string;
  /** economy.featureUnlocks key (player level gate) */
  unlock?: string;
  /** remote-config flag that must be on */
  flag?: string;
  /** needs the API (hidden while it is disabled) */
  online?: boolean;
  /** view kinds (screens / overlays) this feature renders */
  provides: string[];
  /** map rail entry */
  entry?: {
    order: number;
    /** built-in name (calendar, trophy, friends, wheel, mail, cart), an atlas sprite id (`ico_*`) or a small component */
    icon: RailIcon;
    /** i18n key of the label */
    label: string;
    testId: string;
    /** the view kind the entry opens */
    open: string;
    badge?: (a: AppContext) => number | boolean | null;
  };
  /** cheap synchronous check before the map-show hook loads the chunk */
  mapDue?: (a: AppContext) => boolean;
  /** order among map-show hooks (lower first; deliveries 10, results 20, offers 30, prompts 40) */
  mapOrder?: number;
  /** deep-link kinds handled by onLink */
  links?: DeepLink['kind'][];
  /**
   * atlas groups the views draw (`league`, `stickers_*`, `teams`, `evt_harvest`…): loaded in
   * the background when the chunk loads; sprites show a fallback until they arrive
   */
  art?: string[];
  /**
   * cities this feature can bring to the street map's journey (published city packs not on
   * the device): each shows as a teaser that opens the `open` view kind when tapped
   */
  journey?: (a: AppContext) => { city: string; name: string; open: string }[];
  /** the lazy chunk: `() => import('./index.js')` */
  load: () => Promise<{ default: FeatureModule }>;
}

const found = import.meta.glob<{ default: FeatureManifest }>('./*/manifest.ts', { eager: true });
export const FEATURES: FeatureManifest[] = Object.values(found).map((m) => m.default);

const views = new Map<string, View>();
const modules = new Map<string, Promise<FeatureModule>>();

function manifestFor(kind: string): FeatureManifest | undefined {
  return FEATURES.find((f) => f.provides.includes(kind));
}

/** Load a feature's chunk (once) and start its art groups in the background. */
export function load(f: FeatureManifest, a?: Pick<AppContext, 'art'>): Promise<FeatureModule> {
  if (f.art?.length && a) void a.art.loadArtGroups(f.art).catch(() => undefined);
  let p = modules.get(f.id);
  if (!p) {
    p = f.load().then(({ default: m }) => {
      for (const [k, v] of Object.entries(m.views)) views.set(k, v);
      return m;
    });
    p.catch(() => modules.delete(f.id));
    modules.set(f.id, p);
  }
  return p;
}

/** The registered view for a kind (after its feature loaded). */
export function viewFor(kind: string): View | undefined {
  return views.get(kind);
}

export function unlocked(f: FeatureManifest, s: PlayerState, economyUnlocks: Record<string, number>): boolean {
  const lvl = f.unlock ? economyUnlocks[f.unlock] : undefined;
  return lvl === undefined || s.level >= lvl;
}

export function enabled(f: FeatureManifest, a: Pick<AppContext, 'services'>, remote: RemoteConfig): boolean {
  if (f.flag && remote.flags[f.flag] === false) return false;
  if (f.online && !a.services.api.enabled) return false;
  return true;
}

/** Open a feature view (loads its chunk first). */
export async function openView<R = unknown>(a: AppContext, kind: string, props: unknown = {}): Promise<R> {
  const f = manifestFor(kind);
  if (!f) throw new Error(`no feature provides ${kind}`);
  if (!views.has(kind)) {
    a.ui.setBusy(true);
    try {
      await load(f, a);
    } finally {
      a.ui.setBusy(false);
    }
  }
  return a.ui.openAny<R>(kind, props);
}

/** Map rail entries for the features the player has and may use. */
export function railEntries(a: AppContext, open: (kind: string) => void): RailEntry[] {
  const s = a.services.store.state;
  return FEATURES.filter((f) => f.entry && unlocked(f, s, economyUnlocks(a)) && enabled(f, a, a.remote))
    .sort((x, y) => x.entry!.order - y.entry!.order)
    .map((f) => ({
      id: f.id,
      label: f.entry!.label,
      icon: f.entry!.icon,
      testId: f.entry!.testId,
      badge: f.entry!.badge?.(a) ?? null,
      onOpen: () => open(f.entry!.open),
    }));
}

/** Teaser cities the features the player has and may use offer the journey map, in feature order. */
export function journeyHints(a: AppContext): { city: string; name: string; open: string }[] {
  const s = a.services.store.state;
  return FEATURES.filter((f) => f.journey && unlocked(f, s, economyUnlocks(a)) && enabled(f, a, a.remote)).flatMap((f) => f.journey!(a));
}

function economyUnlocks(_a: AppContext): Record<string, number> {
  return economy.featureUnlocks;
}

/** Map-show hooks in `mapOrder` order (ties by id), so a new feature only touches its manifest. */
export function mapHookOrder(features: FeatureManifest[] = FEATURES): string[] {
  return features
    .filter((f) => f.mapDue)
    .sort((x, y) => (x.mapOrder ?? 100) - (y.mapOrder ?? 100) || (x.id < y.id ? -1 : 1))
    .map((f) => f.id);
}

/** The map-show hooks of the current map visit have all run (and none is running). */
let mapHooksDone = false;

/** Run one feature's map hook if it is due; true when it ran. */
async function runHook(a: AppContext, id: string): Promise<boolean> {
  const f = FEATURES.find((x) => x.id === id);
  if (!f?.mapDue || !enabled(f, a, a.remote) || !unlocked(f, a.services.store.state, economyUnlocks(a))) return false;
  if (!f.mapDue(a)) return false;
  try {
    const m = await load(f, a);
    await m.onMapShow?.(a);
  } catch (e) {
    a.services.analytics.track('error', { where: `map_hook:${id}`, message: (e as Error).message });
  }
  return true;
}

/**
 * Run every due map-show hook, one at a time (each loads its own chunk). A hook that became
 * due while the others ran (a link resolved, a sync answer arrived) gets one more look at the end.
 */
export async function runMapHooks(a: AppContext, order: string[] = mapHookOrder()): Promise<void> {
  mapHooksDone = false;
  try {
    const ran = new Set<string>();
    for (const id of order) if (await runHook(a, id)) ran.add(id);
    for (const id of order) if (!ran.has(id)) await runHook(a, id);
  } finally {
    mapHooksDone = true;
  }
}

/** The street map is up and calm: its hooks ran, no view is open, nothing is loading. */
export function mapIdle(a: AppContext): boolean {
  return mapHooksDone && !!a.game?.scene.isActive('map') && a.ui.modals.length === 0 && !a.ui.busy;
}

/**
 * A feature's map hook became due while the map is already showing (a link opened the running
 * app, an answer arrived after the hooks ran): run it now when the map is idle. Otherwise it
 * waits for the running hooks or the next map show. True when it ran.
 */
export async function runMapHookNow(a: AppContext, id: string): Promise<boolean> {
  if (!mapIdle(a)) return false;
  return runHook(a, id);
}

/** Hand a deep link to the feature that owns it. */
export async function handleLink(a: AppContext, link: DeepLink): Promise<void> {
  const f = FEATURES.find((x) => x.links?.includes(link.kind));
  if (!f) return;
  try {
    const m = await load(f, a);
    await m.onLink?.(a, link);
  } catch (e) {
    a.services.analytics.track('error', { where: `link:${link.kind}`, message: (e as Error).message });
  }
}
